/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { t } from '@sportninja/common/i18n'
import parsers from '@sportninja/common/reducers/parsers'
import { connect } from 'react-redux'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { font } from 'src/components/css'
import { NewMapCard } from '../NewMapCard'
import ListStatusView from '../List/StatusView'
import useGetListItems from '../List/useGetListItems'
import { Flex } from '../Layout'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import typeActions from '@sportninja/common/actions/types'
import useWindowSize from 'src/hooks/useWindowSize'
import LocationModal from '../LocationModal'
import DeleteOfficialModal from '../OfficialModal/DeleteOfficialModal'

const sorter = (a, b) => {
  const nameA = a.name_full,
    nameB = b.name_full
  if (nameA < nameB) {
    return -1
  } else if (nameA > nameB) {
    return 1
  }

  return 0
}

const Venues = ({
  entityType,
  id,
  slug,
  refreshKey,
  triggerRefresh,
  query,
  parentPermission,
  readCountries,
  organizationId,
  organizationName,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const [showAllLocations, setShowAllLocations] = useState(true)

  const activeQuery = useMemo(() => {
    if (showAllLocations) {
      const { recursive, ...rest } = query || {}
      return rest
    }
    return { ...query, recursive: 0 }
  }, [query, showAllLocations])

  const { loading, error, items, sort, direction, getItems } = useGetListItems(
    slug,
    { query: activeQuery }
  )
  const [selectedVenue, setSelectedVenue] = useState(null)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)

  // Delete modal state
  const [venueToDelete, setVenueToDelete] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const entity = useMemo(
    () => (entityType === ENTITY_TYPES.org ? 'organizations' : 'schedules'),
    [entityType]
  )

  const updatePermission =
    parentPermission?.update || parentPermission?.admin ? true : false

  useEffect(() => {
    getItems(1, activeQuery)
  }, [refreshKey, sort, direction, showAllLocations])

  const handleDeleteClick = useCallback((venue) => {
    setVenueToDelete(venue)
    setDeleteError(null)
    setIsDeleteModalOpen(true)
  }, [])

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false)
    setVenueToDelete(null)
    setDeleteError(null)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!venueToDelete) {
      return
    }

    try {
      setIsDeleting(true)
      setDeleteError(null)
      await req(`/${entity}/${id}/venues/${venueToDelete.id}`, {
        method: 'DELETE',
      })
      setIsDeleteModalOpen(false)
      setVenueToDelete(null)
      triggerRefresh()
    } catch (error) {
      setDeleteError(error?.message || 'Failed to delete location')
    } finally {
      setIsDeleting(false)
    }
  }, [venueToDelete, entity, id, triggerRefresh])

  useEffect(() => {
    readCountries()
  }, [])

  return (
    <>
      {entityType === ENTITY_TYPES.schedule && (
        <div
          css={css`
            display: inline-flex;
            align-items: center;
            border: 1px solid ${colors.SECONDARY_200};
            border-radius: 8px;
            overflow: hidden;
            margin-top: 16px;
            ${isMobile ? 'margin-left: 16px;' : ''}
          `}
        >
          {[
            { label: 'All', value: true },
            {
              label: `${organizationName ? organizationName : 'Organization'}`,
              value: false,
            },
          ].map((option) => {
            const isActive = showAllLocations === option.value
            return (
              <button
                key={option.label}
                type='button'
                onClick={() => setShowAllLocations(option.value)}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0 12px;
                  height: 33.6px;
                  border: none;
                  min-width: 60px;
                  background: ${isActive
                    ? colors.SECONDARY_300
                    : colors.SECONDARY_500};
                  cursor: pointer;
                  transition: background 0.15s ease;

                  &:hover {
                    background: ${isActive
                      ? colors.SECONDARY_300
                      : colors.SECONDARY_400};
                  }
                `}
              >
                <p
                  css={css`
                    ${font.body}
                    font-size: 13px;
                    font-weight: ${isActive ? 600 : 400};
                    line-height: 1;
                    color: ${isActive ? colors.WHITE : colors.NEUTRAL_50};
                    margin: 0;
                    white-space: nowrap;
                  `}
                >
                  {option.label}
                </p>
              </button>
            )
          })}
        </div>
      )}
      <Flex
        row
        flexWrap
        css={css`
          gap: 20px;
          padding-top: 16px;
          justify-content: flex-start;
          ${isMobile
            ? css`
                padding: 0 16px;
                padding-top: 16px;
              `
            : null}
        `}
      >
        {loading || error || items.length === 0 ? (
          <div style={{ margin: '0 auto' }}>
            <ListStatusView
              loading={loading}
              error={error}
              zeroItems={items.length === 0}
              noItemsText={t('common:location_plural', { count: 2 })}
            />
          </div>
        ) : (
          items.sort(sorter).map((item) => {
            const venue = parsers.venue(item)
            return (
              <NewMapCard
                name={venue?.fullName}
                description={venue?.addressString}
                latitude={venue?.coords?.[0] || null}
                longitude={venue?.coords?.[1] || null}
                key={venue.id}
                onDeleteClick={() => handleDeleteClick(venue)}
                onUpdateClick={() => {
                  setSelectedVenue(venue)
                  setIsLocationModalOpen(true)
                }}
                updatePermission={updatePermission}
                timeZone={venue?.timezone}
                isMobile={isMobile}
              />
            )
          })
        )}
      </Flex>
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => {
          setIsLocationModalOpen(false)
          setSelectedVenue(null)
        }}
        organizationId={organizationId}
        initialState={
          selectedVenue
            ? {
                id: selectedVenue?.id,
                name: selectedVenue?.fullName || '',
                address_line1: selectedVenue?.address?.street_1 || '',
                address_line2: selectedVenue?.address?.street_2 || '',
                city: selectedVenue?.address?.city || '',
                postcode: selectedVenue?.address?.postal_code || '',
                country_id: selectedVenue?.address?.country_id,
                province_id: selectedVenue?.address?.province_id,
                country: selectedVenue?.address?.country?.iso_3166_2 || '',
                province: selectedVenue?.address?.province?.iso_3166_2 || '',
              }
            : undefined
        }
        initialFacilities={selectedVenue?.facilities || []}
        onSuccess={() => {
          setIsLocationModalOpen(false)
          setSelectedVenue(null)
          triggerRefresh()
        }}
      />
      <DeleteOfficialModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        officialName={venueToDelete?.fullName || ''}
        isLoading={isDeleting}
        error={deleteError}
        title='Remove Location'
        message={`Are You Sure You Want To Remove ${
          venueToDelete?.fullName || 'This Location'
        }? This Action Cannot Be Undone.`}
      />
    </>
  )
}

Venues.propTypes = {
  entityType: PropTypes.string,
  id: PropTypes.string.isRequired,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  venues: PropTypes.array,
}

const mapDispatchToProps = (dispatch) => {
  return {
    readCountries: bindActionToPromise(dispatch, typeActions.countries.request),
  }
}

export default connect(null, mapDispatchToProps)(Venues)
